import type { EarningsRange } from "@pax/api-contract/accounting/v1alpha1";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Session } from "@/features/auth/queries";
import { orpc } from "@/lib/api";

/** A recipient, a preset, or one recipient's share of a preset. */
export interface EarningsFilter {
  recipientUserId?: string;
  presetId?: string;
}

const earningsQuery = (
  { recipientUserId, presetId }: EarningsFilter,
  range: EarningsRange,
  back: number,
) => ({
  ...(recipientUserId ? { userId: recipientUserId } : {}),
  ...(presetId ? { presetId } : {}),
  range,
  ...(back > 0 ? { back } : {}),
});

export const earningsByDayQuery = (filter: EarningsFilter, range: EarningsRange, back = 0) =>
  orpc.accounting.dailyEarnings.list.queryOptions({
    input: { query: earningsQuery(filter, range, back) },
    placeholderData: keepPreviousData,
  });

/** Whether the signed-in account has ever been paid a creator reward. False while unknown. */
export function useHasEarnings(user: Session["user"] | undefined): boolean {
  const { data } = useQuery(
    orpc.accounting.players.earningsStatus.get.queryOptions({
      input: { params: { userId: user?.id ?? "" } },
      enabled: user !== undefined && !user.isAnonymous,
      staleTime: 5 * 60_000,
    }),
  );
  return data?.hasEarnings === true;
}
